import { z } from 'zod';

/**
 * Zod schemas for every endpoint `src/pages/empresa/storage-api.ts` calls: `GET/PATCH /storage/me`,
 * `POST /auth/register-storage`, and the `/storage/documents/*` trio. Phase 3.1 of
 * `docs/superpowers/plans/2026-10-14-harness/roadmap.md` — the harness-replication spec's "highest
 * value" piece (`docs/TECHNICAL-AUDIT.md` F-001): a hand-written TypeScript interface is a
 * compile-time promise with nothing checking it at runtime, which is exactly what let `ff309b7`'s
 * envelope-unwrapping bug ship behind a fully green test suite. These schemas make the wire shape
 * itself the thing checked on every real response.
 *
 * GROUND TRUTH: every shape below was read directly off `nexvia-api-web`'s real handler/controller
 * source this session (paths cited per-schema), not copied from this repo's own prior hand-written
 * interfaces or docblocks in `storage-api.ts`/`src/pages/empresa/AGENTS.md` — several of which
 * turned out to be subtly wrong once actually re-checked (see `UpdateStorageProfileResultSchema`'s
 * and `RegisterStorageResultSchema`'s own comments below for the two real discrepancies this found).
 */

// --- Shared --------------------------------------------------------------------------------------

/**
 * `storage_profiles.approval_status`'s CHECK constraint —
 * `nexvia-api-web/src/database/migrations/1788700000000-CreateStorageProfiles.js`, re-confirmed
 * against `storage-profile.entity.ts`'s own `checks:` block (`CK_storage_profiles_approval_status`)
 * and `get-storage-profile.handler.ts`'s/`register-storage.ts`'s own read/write of this column.
 */
export const ApprovalStatusSchema = z.enum(['incomplete', 'pending_review', 'verified', 'rejected', 'suspended']);
export type ApprovalStatus = z.infer<typeof ApprovalStatusSchema>;

/** `STORAGE_STEP_INFO`'s value shape — `nexvia-api-web/src/modules/storage/constants/storage-step.constants.ts`. */
export const StorageNextStepSchema = z.object({
  code: z.string(),
  message: z.string(),
  path: z.string()
});
export type StorageNextStep = z.infer<typeof StorageNextStepSchema>;

// --- GET /storage/me -------------------------------------------------------------------------------

/**
 * `GET /storage/me`'s response — read directly off
 * `nexvia-api-web/src/modules/storage/handlers/get-storage-profile.handler.ts`'s own `return`
 * statement (2026-10-14, re-confirmed this session): bare `{id, razonSocial, rut, email, phone,
 * approvalStatus, rejectionReason, nextStep, breakdown}` — all nine always present, no optional
 * field on this handler's return at all (`storage.controller.ts`'s `getMe` forwards the handler's
 * result with `res.status(200).json(result)`, no wrapping). NEX-1164 (2026-10-16): the handler now
 * also returns `submittedAt`, `reviewedAt`, `contactEmail` and `contactPhone` (see each below), and
 * a logged-in account with no profile gets 404 `STORAGE_PROFILE_NOT_FOUND` — the panel's signal to
 * send it to `/registro` (`storage-api.ts`'s `isStorageProfileMissing`).
 *
 * `address`/`billsWithIva` stay optional here, never required: `storage_profiles` has no column for
 * either yet (`storage-profile.entity.ts`'s real column list has only `business_name`/`rut`/
 * `approval_status`/`submitted_at`/`reviewed_at`/`rejection_reason` plus the `user` relation this
 * handler reads `email`/`phone` off of — no `address`/`bills_with_iva` anywhere). Kept so
 * `FiscalDataForm`'s existing, already-documented optional-render path (`PENDING-DECISION` in the
 * prior hand-written type) doesn't regress — not because the real handler ever sends them.
 */
export const StorageProfileSchema = z.object({
  id: z.number(),
  razonSocial: z.string(),
  rut: z.string(),
  email: z.string(),
  phone: z.string(),
  approvalStatus: ApprovalStatusSchema,
  rejectionReason: z.string().nullable(),
  /**
   * Added 2026-10-14 for the "Mi empresa" reskin's verified-account banner ("Alta aprobada el
   * [fecha]"). Ground truth re-confirmed directly against `nexvia-api-web`'s live, uncommitted
   * `get-storage-profile.handler.ts`: a real `storage_profiles.reviewed_at` column has existed
   * since this table's own migration, but that handler never returned it until this same pass —
   * purely additive on that side too (no migration, no entity change).
   *
   * `.optional()`, like `address`/`billsWithIva` above, for the same reason: `PATCH /storage/me`'s
   * response (`UpdateStorageProfileResultSchema` below) still doesn't compute this field, so
   * leaving it required would force a fabricated default through that transform and every existing
   * `StorageProfile` test fixture in this repo that predates this field. In practice it is only
   * ever read for an already-`verified` profile, which is never reached through a `PATCH` response
   * anyway (`FiscalDataForm` renders its read-only summary instead of the editable form once
   * `approvalStatus` is `verified`, so no save ever happens while this field would matter).
   *
   * KNOWN CAVEAT (see the handler's own docblock for the full account): the one real place that
   * transitions an account to `verified` today, `approve-account.handler.ts`, does not yet stamp
   * this column — so it reads `null` for every account approved through that endpoint until that
   * separate, deliberately-unfixed gap is closed. The UI never fabricates a date for this case; it
   * just omits the date from its headline.
   */
  reviewedAt: z.string().nullable().optional(),
  /**
   * NEX-1164 — set when the third required document is confirmed and the account moves to
   * `pending_review` (`confirm-upload.handler.ts`); `null` before that. ISO datetime.
   */
  submittedAt: z.string().nullable().optional(),
  /**
   * NEX-1164 — where the quote-request emails go (`domain/storage-profile-fields.ts`'s
   * `quoteRequestEmail()`); `null` = the login `email`/`phone` above. Editable through
   * `PATCH /storage/me`, unlike the login email.
   *
   * `submittedAt`/`contactEmail`/`contactPhone` are `.optional()` although the real handler always
   * sends them: same reasoning as `reviewedAt` above (the `PATCH` response and older fixtures), plus
   * deploy order — a panel released before the API it talks to must not turn every `GET /storage/me`
   * into a `CONTRACT_VIOLATION`.
   */
  contactEmail: z.string().nullable().optional(),
  contactPhone: z.string().nullable().optional(),
  nextStep: StorageNextStepSchema.nullable(),
  breakdown: z.object({ approvalStatus: ApprovalStatusSchema }),
  /**
   * Audit S5 (contract C1, NEX-1329) — what the clients rated this depósito (`client_to_storage`
   * reviews only): `averageRating` with one decimal, `null` with no review yet. Optional for the
   * same deploy-order reason as `contactEmail` (and the `PATCH` response never carries it): absent,
   * Mi empresa just does not show the rating.
   */
  rating: z
    .object({ averageRating: z.number().nullable(), reviewCount: z.number().int().nonnegative() })
    .nullable()
    .optional(),
  address: z.string().optional(),
  billsWithIva: z.boolean().optional()
});
export type StorageProfile = z.infer<typeof StorageProfileSchema>;

// --- PATCH /storage/me -----------------------------------------------------------------------------

/** The 6-field subset `update-storage-profile.handler.ts`'s own `return.storageProfile` actually has. */
const UpdateStorageProfileFieldsSchema = z.object({
  id: z.number(),
  razonSocial: z.string(),
  rut: z.string(),
  email: z.string(),
  phone: z.string(),
  approvalStatus: ApprovalStatusSchema,
  /** NEX-1164 — `update-storage-profile.handler.ts` now returns the quote-request contact too. */
  contactEmail: z.string().nullable().optional(),
  contactPhone: z.string().nullable().optional()
});

/**
 * `PATCH /storage/me`'s response — read directly off `update-storage-profile.handler.ts`'s own
 * `return` (2026-10-14): `{success, message, storageProfile}`, where `storageProfile` is ONLY
 * `{id, razonSocial, rut, email, phone, approvalStatus}` — six fields, not the nine `GET /storage/me`
 * returns. `rejectionReason`/`nextStep`/`breakdown` are never computed by this handler and are never
 * present on this specific response — a genuine, confirmed difference from `StorageProfileSchema`
 * above, not an oversight in this schema.
 *
 * DISCREPANCY FOUND BY THIS MIGRATION: the pre-Zod hand-written
 * `UpdateStorageProfileResult.storageProfile: StorageProfile` type claimed all nine fields were
 * required here — a silent type lie nothing ever checked at runtime. Validating strictly against
 * the full `StorageProfileSchema` would make this schema throw `CONTRACT_VIOLATION` on every single
 * successful save, which would be a new, worse regression than the thing this phase exists to
 * prevent. Instead, `rejectionReason`/`nextStep` are defaulted to `null` and `breakdown` is derived
 * from this same response's own `approvalStatus`, so the public return type can stay the full,
 * familiar `StorageProfile` shape (what `FiscalDataForm`'s `onSaved: (updated: StorageProfile) =>
 * void` already expects) without fabricating anything the backend didn't actually say — `null` /
 * `{approvalStatus}` are exactly what every other already-resolved state in this same contract uses
 * for "nothing further to report." Reported explicitly in this task's final message, not silently
 * absorbed.
 */
export const UpdateStorageProfileResultSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
    storageProfile: UpdateStorageProfileFieldsSchema
  })
  .transform(
    (result): { success: boolean; message: string; storageProfile: StorageProfile } => ({
      ...result,
      storageProfile: {
        ...result.storageProfile,
        rejectionReason: null,
        nextStep: null,
        breakdown: { approvalStatus: result.storageProfile.approvalStatus }
      }
    })
  );
export type UpdateStorageProfileResult = z.infer<typeof UpdateStorageProfileResultSchema>;

// --- POST /auth/register-storage ------------------------------------------------------------------

/**
 * `POST /auth/register-storage`'s response — read directly off
 * `nexvia-api-web/src/modules/auth/handlers/register-storage.ts`'s own `return`
 * (`{userId, storageProfileId, approvalStatus}`) and `auth.controller.ts`'s `registerStorage`
 * method, which spreads that onto `{...result, phoneVerified: req.phoneVerified, role: "storage"}`
 * before a bare `res.status(200).json(...)` (same pattern as the sibling `registerDriver` method
 * right above it in that file). This is NOT a `StorageProfile` — no `id`/`razonSocial`/`rut`/
 * `email`/`phone`/`rejectionReason`/`nextStep`/`breakdown` anywhere on this response.
 *
 * SECOND DISCREPANCY FOUND BY THIS MIGRATION: the pre-Zod hand-written
 * `registerStorage(): Promise<StorageProfile>` return type claimed otherwise. Nothing in this app
 * currently reads a field off this specific return value today — its one caller since NEX-1164,
 * `RegistroPage`, awaits it and navigates to Mi empresa, which re-fetches via `GET /storage/me` —
 * so correcting this type has zero known runtime-behavior impact, but it is the actual,
 * ground-truth shape, not the fictitious one.
 *
 * `phoneVerified` is `.optional()`, not required: `JSON.stringify` (what `res.json` calls
 * underneath) drops an `undefined`-valued key entirely, and `req.phoneVerified` is `undefined` on
 * every path that doesn't go through phone-verification middleware (every path today) — confirmed
 * by reading `auth.controller.ts`'s own spread, not assumed.
 */
export const RegisterStorageResultSchema = z.object({
  userId: z.number(),
  storageProfileId: z.number(),
  approvalStatus: ApprovalStatusSchema,
  phoneVerified: z.boolean().optional(),
  role: z.literal('storage')
});
export type RegisterStorageResult = z.infer<typeof RegisterStorageResultSchema>;

// --- GET /storage/documents ------------------------------------------------------------------------

/** Exactly the three CHECK values in `1788900000000-CreateStorageDocuments.js` (`CK_storage_documents_type`). */
export const StorageDocumentTypeSchema = z.enum(['dgi_certificate', 'fire_department_permit', 'insurance_policy']);
export type StorageDocumentType = z.infer<typeof StorageDocumentTypeSchema>;

/** Exactly that same migration's `CK_storage_documents_status` CHECK — same vocabulary as `driver_documents`. */
export const StorageDocumentStatusSchema = z.enum([
  'uploading',
  'pending_review',
  'approved',
  'rejected',
  'failed',
  'outdated'
]);
export type StorageDocumentStatus = z.infer<typeof StorageDocumentStatusSchema>;

/**
 * One element of `GET /storage/documents`'s `{documents: [...]}` array — read directly off
 * `list-storage-documents.handler.ts`'s own per-document `return` object.
 *
 * THIRD DISCREPANCY FOUND BY THIS MIGRATION: `viewUrl` (a pre-signed GET URL, string or `null`) is a
 * REAL field on every one of these objects today — computed whenever `storageKey` exists and the
 * document's status isn't `uploading`/`failed` — that this repo's prior hand-written
 * `StorageDocument` interface never modeled at all. Not a crash (nothing read it, so it was silently
 * dropped rather than breaking anything), but a real gap this ground-truth read turned up. Modeled
 * here as `.nullable().optional()` rather than required: this keeps several sibling test files this
 * task does not own (`DocumentsCard.test.tsx`, `DocumentUploadRow.test.tsx`, `EmpresaPage.test.tsx`)
 * — whose `StorageDocument` fixtures predate this field — compiling unchanged, while still letting a
 * real response's `viewUrl` flow through for any future consumer that wants it.
 */
export const StorageDocumentSchema = z.object({
  id: z.number(),
  documentType: StorageDocumentTypeSchema,
  mimeType: z.string(),
  fileSize: z.number(),
  status: StorageDocumentStatusSchema,
  rejectionReason: z.string().nullable(),
  uploadedAt: z.string(),
  reviewedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  viewUrl: z.string().nullable().optional()
});
export type StorageDocument = z.infer<typeof StorageDocumentSchema>;

/** `GET /storage/documents`'s full (bare) response — `storage-document.controller.ts`'s `listDocuments`. */
export const ListStorageDocumentsResultSchema = z.object({
  documents: z.array(StorageDocumentSchema)
});
export type ListStorageDocumentsResult = z.infer<typeof ListStorageDocumentsResultSchema>;

// --- POST /storage/documents/upload -----------------------------------------------------------------

/**
 * `request-upload-url.handler.ts`'s `return` when the API received the file itself (2026-10-28) —
 * `{documentId, approvalStatus}`, bare. Only the id is read: the panel refetches the account after
 * the confirm that follows.
 */
export const UploadDocumentResultSchema = z.object({
  documentId: z.number()
});
export type UploadDocumentResult = z.infer<typeof UploadDocumentResultSchema>;

// --- PATCH /storage/documents/:id/confirm -----------------------------------------------------------

/**
 * `confirm-upload.handler.ts`'s own `return` — `{documentId, status, approvalStatus}`, bare.
 * NEX-1164: `approvalStatus` is the account's status after this confirm (the third required
 * document moves `incomplete`/`rejected` to `pending_review`). Optional for the same deploy-order
 * reason as `StorageProfileSchema.contactEmail`; the panel re-reads `GET /storage/me` after every
 * upload anyway.
 */
export const ConfirmUploadResultSchema = z.object({
  documentId: z.number(),
  status: StorageDocumentStatusSchema,
  approvalStatus: ApprovalStatusSchema.optional()
});
export type ConfirmUploadResult = z.infer<typeof ConfirmUploadResultSchema>;
